import type {
  CheckoutAccountPrefillField,
  CheckoutAccountPrefillSnapshot,
} from '@/lib/functions/checkoutAccountPrefill';

const CHECKOUT_FORM_DRAFT_KEY = (token: string) => `partyCheckoutDraft:${token}`;

const TEXT_FIELDS = [
  'shippingFirstName',
  'shippingLastName',
  'shippingPhoneNumber',
  'billingFirstName',
  'billingLastName',
  'billingPhoneNumber',
  'billingEmail',
  'billingStreet',
  'billingStreet2',
  'billingCity',
  'billingProvinceCode',
  'billingPostCode',
  'orderNotes',
] as const;

const CHECKBOX_FIELDS = ['noSubstitutions', 'ageCheck', 'termsCheck'] as const;

export type CheckoutFormDraftTextField = (typeof TEXT_FIELDS)[number];
export type CheckoutFormDraftCheckboxField = (typeof CHECKBOX_FIELDS)[number];

export interface CheckoutFormDraft {
  shippingFirstName?: string;
  shippingLastName?: string;
  shippingPhoneNumber?: string;
  billingFirstName?: string;
  billingLastName?: string;
  billingPhoneNumber?: string;
  billingEmail?: string;
  billingStreet?: string;
  billingStreet2?: string;
  billingCity?: string;
  billingProvinceCode?: string;
  billingPostCode?: string;
  billingCountryCode?: string;
  orderNotes?: string;
  noSubstitutions?: boolean;
  ageCheck?: boolean;
  termsCheck?: boolean;
  selectedShippingMethod?: string;
  accountPrefill?: CheckoutAccountPrefillSnapshot;
  editedAccountFields?: CheckoutAccountPrefillField[];
}

export function readCheckoutFormDraft(token: string | null | undefined): CheckoutFormDraft | null {
  if (typeof window === 'undefined' || !token) {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(CHECKOUT_FORM_DRAFT_KEY(token));
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as CheckoutFormDraft;
  } catch {
    sessionStorage.removeItem(CHECKOUT_FORM_DRAFT_KEY(token));
    return null;
  }
}

export function writeCheckoutFormDraft(token: string, draft: CheckoutFormDraft): void {
  if (typeof window === 'undefined' || !token) {
    return;
  }

  sessionStorage.setItem(CHECKOUT_FORM_DRAFT_KEY(token), JSON.stringify(draft));
}

export function clearCheckoutFormDraft(token: string | null | undefined): void {
  if (typeof window === 'undefined' || !token) {
    return;
  }

  sessionStorage.removeItem(CHECKOUT_FORM_DRAFT_KEY(token));
}

export function collectCheckoutFormDraftFromForm(
  form: HTMLFormElement,
  extras: {
    billingCountryCode: string;
    billingStateCode: string;
    selectedShippingMethod?: string | null;
  },
): CheckoutFormDraft {
  const formData = new FormData(form);
  const draft: CheckoutFormDraft = {
    billingCountryCode: extras.billingCountryCode,
    billingProvinceCode:
      extras.billingStateCode || (formData.get('billingProvinceCode') as string) || '',
    selectedShippingMethod: extras.selectedShippingMethod ?? undefined,
  };

  for (const field of TEXT_FIELDS) {
    const value = formData.get(field);
    if (typeof value === 'string') {
      draft[field] = value;
    }
  }

  for (const field of CHECKBOX_FIELDS) {
    draft[field] = formData.get(field) !== null;
  }

  return draft;
}

export function resolveCheckoutDraftString(
  draft: CheckoutFormDraft | null,
  key: CheckoutFormDraftTextField,
  fallback = '',
): string {
  if (draft && key in draft && typeof draft[key] === 'string') {
    return draft[key] as string;
  }
  return fallback;
}

export function resolveCheckoutDraftCheckbox(
  draft: CheckoutFormDraft | null,
  key: CheckoutFormDraftCheckboxField,
  fallback: boolean,
): boolean {
  if (draft && key in draft && typeof draft[key] === 'boolean') {
    return draft[key] as boolean;
  }
  return fallback;
}
